import React, { useState, useEffect } from "react";
import Page from "src/components/Page";
import {
  Grid,
  Container,
  Typography,
  makeStyles,
  Button,
  Paper,
  IconButton,
  InputBase,
  Link,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  colors,
} from "@material-ui/core";
import {
  CalendarToday as CalendarTodayIcon,
  Category as CategoryIcon,
  Label as LabelIcon,
  Search as SearchIcon,
} from "@material-ui/icons";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { getBlogBaseURL, urlAPIConf } from "src/config/api";
import safeLogger from "src/services/safeLogger";
import { connect } from "react-redux";
import { getBaseLanguage } from "./reports/utils/helpers";
import SubjectIcon from '@material-ui/icons/Subject';

const useStyles = makeStyles((theme) => ({
  spinner: {
    position: "absolute",
    top: "48%",
    left: "48%",
  },
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: "100%",
    paddingBottom: theme.spacing(8),
    paddingTop: theme.spacing(2),
  },
  pageTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 300,
    color: colors.grey[800],
  },
  pageSubtitle: {
    marginBottom: theme.spacing(2),
    color: colors.grey[600],
  },
  inputWrapper: {
    padding: "2px 4px",
    display: "flex",
    alignItems: "center",
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
  articleCard: {
    margin: "auto",
    display: "flex",
    minHeight: 120,
    marginBottom: theme.spacing(3),
    "& .MuiCardContent-root": {
      [theme.breakpoints.up("md")]: {
        width: "75%",
      },
    },
    "& .MuiCardActions-spacing": { marginLeft: "auto" },
    "& .card-meta": {
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(1),
      "& .card-date": {
        verticalAlign: "super",
        marginRight: theme.spacing(1),
      },
      "& .group-icon": {
        marginRight: theme.spacing(1),
      },
      "& .MuiChip-root": {
        verticalAlign: "top",
        marginRight: theme.spacing(1),
        backgroundColor: colors.blueGrey[100],
        "& .MuiChip-label": {
          color: colors.blueGrey[700],
        },
      },
      "&  *": {
        color: theme.palette.grey[400],
        fill: theme.palette.grey[400],
      },
    },
  },
  articleMedia: {
    minHeight: 180,
    minWidth: 180,
    position: "sticky",
    [theme.breakpoints.down("md")]: {
      height: "200px",
    },
  },
  cardActions: {
    minWidth: 140,
    paddingRight: 30,
    [theme.breakpoints.down("sm")]: {
      paddingRight: 0,
    },
  },
  learnMore: {
    height: 50,
    width: 170,
    [theme.breakpoints.down("sm")]: {
      height: 50,
      width: 110,
    },
  },
}));

const mapStateToProps = (state) => {
  return {
    appConf: state.appConf,
  };
};

const ConnectedHelpPage = (props) => {
  const classes = useStyles();
  const { appConf } = props;
  // const [ticketingFormDialogOpen, setTicketingFormDialogOpen] = useState(false);
  const [helpPosts, setHelpPosts] = useState([]);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSearch = (event) => {
    event.preventDefault();
    const query = event.target[0].value.replace(" ", "+");
    navigate(`/${urlAPIConf.SEARCH_SLUG}/?q=${query}`);
  };

  // const handleCloseTicketingFormDialog = () => {
  //   setTicketingFormDialogOpen(false);
  // };

  // const submitSupportTicket = (event) => {
  //   event.preventDefault();
  // };

  useEffect(() => {
    let mounted = true;
    if (appConf.lang) {
      fetch(
        `${getBlogBaseURL()}/${
          urlAPIConf.BASE_API_SLUG
        }/pages/?fields=_,id,title,intro,first_published_at,categories,tags,main_image&type=blog.BlogPage&order=-first_published_at&categories__in=3&locale=${getBaseLanguage()}`
      )
        .then((response) => response.json())
        .then((data) => {
          if (mounted) {
            safeLogger(data.items);
            setHelpPosts(data.items);
          }
        })
        .catch((error) => {
          safeLogger("Fetch error:", error);
        });
    }

    return () => {
      mounted = false;
    };
  }, [appConf]);

  return (
    <Page className={classes.root} title={t("Help and Support")}>
      <Container>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography
              component="h3"
              variant="h3"
              className={classes.pageTitle}
            >
              {t("Help and Support")}
            </Typography>
            <Typography
              component="h6"
              variant="h6"
              className={classes.pageSubtitle}
            >
              {`Massive Analyser v${process.env.REACT_APP_VERSION}`}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12}>
            <Grid item xs={12}>
              <Paper
                component="form"
                className={classes.inputWrapper}
                onSubmit={handleSearch}
              >
                <InputBase
                  className={classes.input}
                  placeholder={t(
                    "help_page_search_placeholder",
                    "Find what you are looking for in our knowledge base..."
                  )}
                  inputProps={{ "aria-label": "search knowledge base" }}
                  fullWidth
                  variant="outlined"
                />
                <IconButton
                  type="submit"
                  className={classes.iconButton}
                  aria-label="search"
                >
                  <SearchIcon />
                </IconButton>
              </Paper>
            </Grid>
            <Trans i18nKey="help-into">
              <Typography style={{ marginTop: "20px", textAlign: "justify" }}>
                We provide comprehensive documentation to help you efficiently
                navigate the workflows and user interface of Massive Analyser.
                You can access the relevant documents below. If you cannot find
                the information you are looking for or have any questions,
                feedback, or suggestions, feel free to contact our support team
                via the live chat widget in the bottom right corner.
              </Typography>
            </Trans>
            <Typography
              component="h4"
              variant="h4"
              style={{
                marginBottom: "20px",
                marginTop: "20px",
                fontWeight: "300",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <SubjectIcon style={{ fontSize: "26px" }} /> {t("Recent articles")}
            </Typography>
            {Boolean(helpPosts.length) &&
              helpPosts.map((article, index) => (
                <Card className={classes.articleCard} key={index}>
                  <CardMedia
                    className={classes.articleMedia}
                    image={
                      article.main_image
                        ? `${getBlogBaseURL()}/${article.main_image.url}`
                        : "/assets/images/misc/placeholder.png"
                    }
                    title={article.title}
                  />
                  <CardContent>
                    <Typography gutterBottom variant="h5" component="h2">
                      <Link href={`/${urlAPIConf.ARTICLES_SLUG}/${article.id}`}>
                        {article.title}
                      </Link>
                    </Typography>
                    <div className="card-meta">
                      <span>
                        <CalendarTodayIcon className="group-icon" />
                        <span className="card-date">
                          {format(
                            new Date(article.meta.first_published_at),
                            "dd/MM/yyyy"
                          )}
                        </span>
                      </span>

                      {Boolean(article.categories.length) && (
                        <span>
                          <CategoryIcon className="group-icon" />
                          {article.categories.map((item, index) => (
                            <Chip
                              key={index}
                              onClick={() =>
                                navigate(
                                  `/${urlAPIConf.SEARCH_SLUG}/?category=${item.id}`
                                )
                              }
                              size="small"
                              label={t(item.name)}
                            />
                          ))}
                        </span>
                      )}
                    </div>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      component="p"
                    >
                      {article.intro}
                    </Typography>
                    <div className="card-meta">
                      {Boolean(article.tags.length) && (
                        <span>
                          <LabelIcon className="group-icon" />
                          {article.tags.map((item, index) => (
                            <Chip
                              key={index}
                              onClick={() =>
                                navigate(
                                  `/${urlAPIConf.SEARCH_SLUG}/?tags=${item}`
                                )
                              }
                              size="small"
                              label={item}
                            />
                          ))}
                        </span>
                      )}
                    </div>
                  </CardContent>
                  <CardActions className={classes.cardActions}>
                    <Button
                      component={RouterLink}
                      to={`/${urlAPIConf.ARTICLES_SLUG}/${article.id}`}
                      size="small"
                      color="primary"
                      className={classes.learnMore}
                    >
                      {t("Learn More")}
                    </Button>
                  </CardActions>
                </Card>
              ))}
          </Grid>
          <Grid item xs={12} sm={3} className={classes.sidebar}></Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export const HelpPage = connect(mapStateToProps)(ConnectedHelpPage);
